import { LeftMenuService } from 'app/store/services/left-menu.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';

// Actions
import { deleteAProduct, refreshMeeting, updateMeeting, viewMeeting } from 'app/store/actions/meeting.action';
import { openDialog, openSnackbar } from 'app/store/actions/page.action';
import { loadPipelines } from 'app/store/actions/pipeline.action';

// Reducers
import { EReducers, IReducers } from 'app/store/reducers';

// Models
import { EMeetingStateStatus } from 'app/store/models/meeting.model';
import { IUserSessionData } from 'app/store/models/user.model';
import { EPipelineStateStatus, IPipeline, IPipelineState } from 'app/store/models/pipeline.model';

// Services
import { SessionService } from 'app/store/services/session.service';

import { AddAssocProductComponent } from '../../dialogs/add-assoc-product/add-assoc-product.component';
import { ITextFieldIconProps } from '../../shared/ui/search/text-field.component';
import { faEdit } from '@fortawesome/free-regular-svg-icons';
import { TimezonePipe } from '../../shared/ui/pipes/timezone.pipe';
import { UtilitiesService } from '../../store/services/utilities.servive';

@Component({
  selector: 'app-edit-meeting',
  templateUrl: './edit-meeting.component.html',
  styleUrls: ['./edit-meeting.component.scss'],
})
export class EditMeetingComponent implements OnInit, OnDestroy {
  meetingData: any;
  sessionData: IUserSessionData;
  formGroup: FormGroup;
  formReady = false;
  contactTarget: string;
  pipelines: IPipeline[];
  loading = false;

  editRepIconProp: ITextFieldIconProps = {
    icon: faEdit,
    hoverText: 'Edit',
    position: 'right',
    updateOnClick: true,
    onClick: () => this.router.navigate([this.contactTarget]),
  };

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private store: Store<IReducers>,
    private router: Router,
    private route: ActivatedRoute,
    private sessionService: SessionService,
    private leftMenuService: LeftMenuService,
    private readonly utilService: UtilitiesService,
  ) {
    this.leftMenuService.expand('inbox');
  }

  ngOnInit() {
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(sessionData => {
        this.sessionData = sessionData;
        this.route.params.pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.store.dispatch(
            viewMeeting({
              id_meeting: Number(this.route.snapshot.paramMap.get('id')),
            }),
          );
        });

        this.store
          .select(EReducers.MEETING)
          .pipe(takeUntil(this.destroy$))
          .subscribe((state: any) => {
            if (state.status === EMeetingStateStatus.READY) {
              this.meetingData = state.data;
              this.contactTarget = `/user_contacts/${this.meetingData.contact_id}/edit`;
              const pipe = new TimezonePipe(this.utilService);
              const date = pipe.transform(
                this.meetingData.meeting_date,
                'YYYY-MM-DD',
                this.utilService.getMomentTimezone(this.sessionData.time_zone),
              );
              const time = pipe.transform(
                this.meetingData.meeting_date,
                'HH:mm',
              );
              if (!this.formGroup) {
                this.formGroup = new FormGroup({
                  name: new FormControl({
                    value: this.meetingData.contact_name,
                    disabled: true,
                  }),
                  company: new FormControl({
                    value: this.meetingData.contact_company,
                    disabled: true,
                  }),
                  phone: new FormControl({
                    value: this.meetingData.contact_email,
                    disabled: true,
                  }),
                  meeting_date: new FormControl(new Date(date), [
                    Validators.required,
                  ]),
                  meeting_time: new FormControl(time, [Validators.required]),
                  meeting_note: new FormControl(
                    this.meetingData.meeting_notes,
                    [Validators.required],
                  ),
                });
              }

              this.store.dispatch(
                loadPipelines({
                  pagination: {
                    current_page: 1,
                    page_size: 10,
                  },
                }),
              );
              this.store
                .select(EReducers.PIPELINE)
                .pipe(takeUntil(this.destroy$))
                .subscribe((pipelineState: IPipelineState) => {
                  if (pipelineState.status === EPipelineStateStatus.UPDATED) {
                    this.store.dispatch(
                      loadPipelines({
                        pagination: pipelineState.pagination,
                      }),
                    );
                  }

                  if (pipelineState.pipelines) {
                    this.pipelines = pipelineState.pipelines;
                  }

                  this.meetingData.products.forEach(item => {
                    this.pipelines.forEach(p => {
                      if (p.id === item.pipeline) {
                        item.pipename = p.pipeline;
                      }
                    });
                  });
                  this.sessionData = sessionData;
                });

              this.formReady = true;
            } else if (state.status === EMeetingStateStatus.UPDATED) {
              this.store.dispatch(
                openSnackbar({
                  snackbarData: {
                    data: {
                      message: 'Meeting details updated successfully!',
                      type: 'success',
                    },
                    verticalPosition: 'bottom',
                  },
                }),
              );
              this.router.navigate([
                '/messages',
                'view',
                'meeting',
                this.route.snapshot.paramMap.get('id'),
              ]);
            }
          });
      });
  }

  ngOnDestroy() {
    if (this.destroy$) {
      this.destroy$.next(false);
    }
  }

  submit() {
    const form = this.formGroup.getRawValue();
    const pipe = new TimezonePipe(this.utilService);
    this.store.dispatch(
      updateMeeting({
        meeting: {
          meeting_id: Number(this.route.snapshot.paramMap.get('id')),
          meeting_date: pipe.transform(
            form.meeting_date,
            'YYYY-MM-DD',
          ),
          meeting_time: form.meeting_time,
          meeting_note: form.meeting_note,
        },
      }),
    );
    this.store.dispatch(
      refreshMeeting({
        id_meeting: Number(this.route.snapshot.paramMap.get('id')),
      }),
    );
  }

  back() {
    this.router.navigate([
      `/messages/view/meeting/${this.route.snapshot.paramMap.get('id')}`,
    ]);
  }

  add() {
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: 'Add new product',
          content: AddAssocProductComponent,
          componentData: {
            data: {
              meeting: {
                id: this.route.snapshot.paramMap.get('id'),
              },
            },
            action: 'new',
          },
          dialogMinWidth: '200px',
          canClose: true,
        },
      }),
    );
  }

  update(item) {
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: 'Update product',
          content: AddAssocProductComponent,
          componentData: {
            data: {
              meeting: {
                id: this.route.snapshot.paramMap.get('id'),
              },
              product: item,
            },
            action: 'update',
          },
          dialogMinWidth: '200px',
          canClose: true,
        },
      }),
    );
  }

  deleteProduct(product) {
    this.store.dispatch(
      deleteAProduct({
        meeting: {
          meeting_id: Number(this.route.snapshot.paramMap.get('id')),
        },
        product,
      }),
    );

    this.store.dispatch(
      refreshMeeting({
        id_meeting: Number(this.route.snapshot.paramMap.get('id')),
      }),
    );
  }

  isChanged() {
    return this.formGroup.dirty || this.formGroup.touched;
  }
}
